import modelBaseV2 from "../model-base-v2.model";

/**
 * Incentive Approval Model
 *
 * API wrappers for the 3 FFIS approval routes. Menu WM021104, APPROVER.
 *
 * Country and user are NOT sent from here - the backend reads them from the session, and the
 * submitter/approver identity is taken from the token. That matters here more than anywhere
 * else in FFIS: the separation-of-duties check below depends on the server knowing who is
 * calling, not on the client saying so.
 *
 * ============================================================================================
 * ⚠️ ALL THREE ROUTES SHARE ONE MENU, SO PERMISSIONS CANNOT SEPARATE SUBMIT FROM SIGN-OFF
 * ============================================================================================
 * Whoever can submit can also sign off, and no configuration changes that - it needs the two
 * routes on SEPARATE MENUS, which is the role-security owner's decision.
 *
 * WHAT *IS* ENFORCED, in the stored procedure: THE PERSON WHO SUBMITTED CANNOT SIGN OFF. That
 * holds however it is called, including directly in SQL. It is not four-eyes though: one person
 * can submit, have a colleague sign, then sign the colleague's next submission.
 *
 * ⚠️ AND WM021103 (Calculation) MUST NOT GO TO THE SAME ROLE AS THIS, or whoever runs the payout
 * calculation also approves it.
 *
 * @author FFIS
 */
const IncentiveApprovalModel = {

  /**
   * The approval chain for a scope, plus the period's cutoff status.
   *
   * Two result sets, and an approver needs both: what has been signed, and whether the inputs
   * behind it are frozen. A period still OPEN means the figures can move after sign-off.
   *
   * ⚠️ SHOW staleWarning WHEN IT IS NOT EMPTY. It means a newer completed run exists than the
   * one approved, so the approved figures are no longer the latest. The approval still stands
   * for the run it NAMES - that is why the run id is recorded - but anybody reading current
   * numbers is not reading approved ones.
   */
  searchApprovals: (filters) => {
    const queryString = modelBaseV2.toQueryString(filters);
    return modelBaseV2.get(`/api/incentive/approval?${queryString}`);
  },

  /**
   * Submit a completed run for sign-off. A REQUEST, not a decision - it writes nothing to the
   * results and locks nothing.
   *
   * Refused for: a run that is not COMPLETED; a SIMULATION (approving one would turn a what-if
   * into an entitlement); a BATCH_NIGHTLY run (FR-03 exempt, so nobody reviewed its inputs); a
   * run that wrote NO employee results (reads as "approved, nothing to pay"); a run that already
   * has a PENDING or APPROVED approval.
   *
   * Each of those is a real validation result, not a bug - surface the message rather than
   * retrying.
   */
  submit: (body) => {
    return modelBaseV2.post('/api/incentive/approval/submit', { body });
  },

  /**
   * APPROVE or REJECT.
   *
   * ⚠️ APPROVING IS IRREVERSIBLE IN PRACTICE. In one transaction it:
   *   1. records that a named person accepted a named set of figures;
   *   2. writes SupersededByRunId on every other completed run - THE ONLY PLACE IN FFIS that
   *      resolves which run is authoritative, since every reader otherwise falls back to "the
   *      highest completed run";
   *   3. writes IncentiveFinalPayout, one LOCKED row per employee, as the immutable record of
   *      what was payable regardless of any later recalculation;
   *   4. closes the period (by default), so no upload or calculation can change it.
   *
   * NOTHING UNDOES IT. No route re-opens a closed period or unlocks a payout, and there is no
   * decision yet about what happens if an approved figure turns out to be wrong.
   *
   * REJECT requires a comment - without a reason the preparer has nothing to act on and the
   * record cannot say why a payout was held.
   *
   * closePeriod defaults to true on the server. Pass false for a subsidiary that approves weekly
   * and wants the month left open until the last week is signed - accepting that those figures
   * can still move.
   */
  signoff: (body) => {
    return modelBaseV2.post('/api/incentive/approval/signoff', { body });
  },
};

export default IncentiveApprovalModel;
